import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { CommonNumberInput } from '@/components/Common/CommonNumberInput';
import { formatPrice } from '@/utils/helper';
import type { RealEstateOwnedRow } from '@/types/PfsType';

const STATUS_OPTIONS: { value: RealEstateOwnedRow['status']; label: string }[] =
  [
    { value: 'owner_occupied', label: 'Owner-occupied' },
    { value: 'rental', label: 'Rental' },
    { value: 'held_for_sale', label: 'Held for sale' },
  ];

const PROPERTY_TYPE_OPTIONS: {
  value: RealEstateOwnedRow['propertyType'];
  label: string;
}[] = [
  { value: 'single_family', label: 'Single family' },
  { value: 'multifamily', label: 'Multifamily' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'other', label: 'Other' },
];

function emptyRealEstateRow(): RealEstateOwnedRow {
  return {
    propertyAddress: '',
    status: 'rental',
    propertyType: 'single_family',
    pctOwned: 100,
    acquisitionDate: '',
    acquisitionCost: 0,
    marketValue: 0,
    mortgage1: {},
    mortgage2: {},
    monthlyRent: 0,
    monthlyMortgagePmt: 0,
    monthlyTaxesInsMaint: 0,
    ownershipEntity: '',
    remarks: '',
  };
}

function netRentalIncome(row: RealEstateOwnedRow): number {
  return (
    (Number(row.monthlyRent) || 0) -
    (Number(row.monthlyMortgagePmt) || 0) -
    (Number(row.monthlyTaxesInsMaint) || 0)
  );
}

type Props = {
  rows: RealEstateOwnedRow[];
  onChange: (rows: RealEstateOwnedRow[]) => void;
  readOnly?: boolean;
};

export function ScheduleOfRealEstateOwnedForm({
  rows,
  onChange,
  readOnly,
}: Props) {
  const updateRow = (idx: number, patch: Partial<RealEstateOwnedRow>) => {
    const next = [...rows];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const updateMortgage = (
    idx: number,
    which: 'mortgage1' | 'mortgage2',
    patch: Partial<RealEstateOwnedRow['mortgage1']>
  ) => {
    const next = [...rows];
    next[idx] = {
      ...next[idx],
      [which]: { ...next[idx][which], ...patch },
    };
    onChange(next);
  };

  type Totals = {
    marketValue: number;
    mortgageBalance: number;
    netRentalIncome: number;
  };
  const totals = rows.reduce<Totals>(
    (acc, row) => ({
      marketValue: acc.marketValue + (Number(row.marketValue) || 0),
      mortgageBalance:
        acc.mortgageBalance +
        (Number(row.mortgage1?.balance) || 0) +
        (Number(row.mortgage2?.balance) || 0),
      netRentalIncome: acc.netRentalIncome + netRentalIncome(row),
    }),
    { marketValue: 0, mortgageBalance: 0, netRentalIncome: 0 }
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Schedule of Real Estate Owned
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every property you currently own — this is separate from the subject
        property on any individual loan file.
      </Typography>

      {rows.map((row, idx) => (
        <Box
          key={row._id ?? idx}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Property address"
                disabled={readOnly}
                value={row.propertyAddress ?? ''}
                onChange={(e) =>
                  updateRow(idx, { propertyAddress: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                disabled={readOnly}
                value={row.status ?? 'rental'}
                onChange={(e) =>
                  updateRow(idx, {
                    status: e.target.value as RealEstateOwnedRow['status'],
                  })
                }
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Property type"
                disabled={readOnly}
                value={row.propertyType ?? 'single_family'}
                onChange={(e) =>
                  updateRow(idx, {
                    propertyType: e.target
                      .value as RealEstateOwnedRow['propertyType'],
                  })
                }
              >
                {PROPERTY_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} md={2}>
              <CommonNumberInput
                fullWidth
                size="small"
                label="% owned"
                disabled={readOnly}
                value={row.pctOwned ?? ''}
                onChange={(e) =>
                  updateRow(idx, { pctOwned: Number(e.target.value) || 0 })
                }
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Acquisition date"
                disabled={readOnly}
                value={row.acquisitionDate ?? ''}
                onChange={(e) =>
                  updateRow(idx, { acquisitionDate: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <CommonNumberInput
                fullWidth
                size="small"
                label="Acquisition cost"
                disabled={readOnly}
                value={row.acquisitionCost ?? ''}
                onChange={(e) =>
                  updateRow(idx, {
                    acquisitionCost: Number(e.target.value) || 0,
                  })
                }
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <CommonNumberInput
                fullWidth
                size="small"
                label="Market value"
                disabled={readOnly}
                value={row.marketValue ?? ''}
                onChange={(e) =>
                  updateRow(idx, { marketValue: Number(e.target.value) || 0 })
                }
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                1st mortgage
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <CommonNumberInput
                fullWidth
                size="small"
                label="Balance"
                disabled={readOnly}
                value={row.mortgage1?.balance ?? ''}
                onChange={(e) =>
                  updateMortgage(idx, 'mortgage1', {
                    balance: Number(e.target.value) || 0,
                  })
                }
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Lender / loan #"
                disabled={readOnly}
                value={row.mortgage1?.lenderLoanNumber ?? ''}
                onChange={(e) =>
                  updateMortgage(idx, 'mortgage1', {
                    lenderLoanNumber: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Maturity date"
                disabled={readOnly}
                value={row.mortgage1?.maturityDate ?? ''}
                onChange={(e) =>
                  updateMortgage(idx, 'mortgage1', {
                    maturityDate: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <CommonNumberInput
                fullWidth
                size="small"
                label="Rate %"
                disabled={readOnly}
                value={row.mortgage1?.interestRatePct ?? ''}
                onChange={(e) =>
                  updateMortgage(idx, 'mortgage1', {
                    interestRatePct: Number(e.target.value) || 0,
                  })
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={2}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(row.mortgage1?.recourse)}
                    disabled={readOnly}
                    onChange={(e) =>
                      updateMortgage(idx, 'mortgage1', {
                        recourse: e.target.checked,
                      })
                    }
                  />
                }
                label="Recourse"
              />
            </Grid>

            <Grid item xs={6} md={4}>
              <CommonNumberInput
                fullWidth
                size="small"
                label="Monthly rent"
                disabled={readOnly}
                value={row.monthlyRent ?? ''}
                onChange={(e) =>
                  updateRow(idx, { monthlyRent: Number(e.target.value) || 0 })
                }
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <CommonNumberInput
                fullWidth
                size="small"
                label="Monthly mortgage pmt"
                disabled={readOnly}
                value={row.monthlyMortgagePmt ?? ''}
                onChange={(e) =>
                  updateRow(idx, {
                    monthlyMortgagePmt: Number(e.target.value) || 0,
                  })
                }
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <CommonNumberInput
                fullWidth
                size="small"
                label="Monthly taxes, ins & maint"
                disabled={readOnly}
                value={row.monthlyTaxesInsMaint ?? ''}
                onChange={(e) =>
                  updateRow(idx, {
                    monthlyTaxesInsMaint: Number(e.target.value) || 0,
                  })
                }
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>

            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Ownership entity"
                disabled={readOnly}
                value={row.ownershipEntity ?? ''}
                onChange={(e) =>
                  updateRow(idx, { ownershipEntity: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="body2" sx={{ mt: 1.5 }}>
                Net rental income:{' '}
                <strong>{formatPrice(netRentalIncome(row), 0)}</strong>/mo
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              {!readOnly && (
                <IconButton
                  aria-label="Remove property"
                  onClick={() => onChange(rows.filter((_, i) => i !== idx))}
                >
                  <i className="fa-light fa-trash" />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Box>
      ))}

      {!readOnly && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => onChange([...rows, emptyRealEstateRow()])}
          sx={{ mb: 1.5 }}
        >
          Add property
        </Button>
      )}

      {rows.length > 0 && (
        <Typography variant="body2" fontWeight={600}>
          Totals — market value: {formatPrice(totals.marketValue, 0)} · mortgage
          balance: {formatPrice(totals.mortgageBalance, 0)} · net rental income:{' '}
          {formatPrice(totals.netRentalIncome, 0)}/mo
        </Typography>
      )}
    </Box>
  );
}

export { emptyRealEstateRow, netRentalIncome };
